import React, { useState } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  TextInputProps,
} from 'react-native';
import { Text } from '@/components/Text';
import Icon from '@expo/vector-icons/FontAwesome';
import { styles } from './style';
import colors from '@/config/colors';

interface InputProps extends Omit<TextInputProps, 'onChange'> {
  label?: string;
  value: string;
  onChange: (text: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  secureTextEntry?: boolean;
  statusMessage?: string;
  status?: 'default' | 'warning' | 'validated';
  additionalInfo?: string;
  maxLength?: number;
  multiline?: boolean;
  numberOfLines?: number;
  disabled?: boolean;
  required?: boolean;
  testID?: string;
}

export const Input = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  secureTextEntry = false,
  status = 'default',
  statusMessage,
  additionalInfo,
  maxLength,
  multiline = false,
  numberOfLines,
  disabled = false,
  required = false,
  testID,
  style,
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [secure, setSecure] = useState(secureTextEntry);

  const isActive = isTyping && value.length > 0;
  const isWarning = status === 'warning' && !isActive;
  const isValidated = status === 'validated' && !isActive;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    setIsTyping(false);
    onBlur?.();
  };
  const handleChange = (text: string) => {
    setIsTyping(true);
    onChange(text);
  };

  //----Styles-----//
  const outerStyles = [
    styles.outer,
    isFocused || isActive ? styles.outerFocused : {},
  ];

  const innerStyles = [
    styles.inner,
    !multiline && styles.innerSingleLine,
    disabled
      ? styles.innerDisabled
      : isActive
        ? styles.innerActive
        : isWarning
          ? styles.innerWarning
          : isValidated
            ? styles.innerValidated
            : {},
  ];

  const renderIcon = () => {
    if (secureTextEntry) {
      return (
        <TouchableOpacity onPress={() => setSecure(!secure)}>
          <View style={styles.eyeIconWrapper}>
            <Icon name={secure ? 'eye-slash' : 'eye'} style={styles.eyeIcon} />
          </View>
        </TouchableOpacity>
      );
    }
    if (status === 'warning') {
      return (
        <View style={styles.warningIconWrapper}>
          <Icon name="exclamation" style={styles.icon} />
        </View>
      );
    }

    if (status === 'validated') {
      return (
        <View style={styles.validatedIconWrapper}>
          <Icon name="check" style={styles.icon} />
        </View>
      );
    }

    return null;
  };

  return (
    <View style={[styles.container, style]}>
      {label && (
        <Text style={styles.label}>
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
      )}
      {additionalInfo && <Text style={styles.info}>{additionalInfo}</Text>}

      <View style={outerStyles}>
        <View style={innerStyles}>
          <TextInput
            allowFontScaling={false}
            style={[styles.input, disabled && styles.disabledInput]}
            value={value}
            onChangeText={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            placeholderTextColor={colors.newUI.therapist.grey70}
            secureTextEntry={secure}
            editable={!disabled}
            maxLength={maxLength}
            multiline={multiline}
            numberOfLines={numberOfLines}
            testID={testID}
          />
          {renderIcon()}
        </View>
      </View>

      {status === 'warning' && statusMessage && (
        <Text style={styles.warningText}>{statusMessage}</Text>
      )}
    </View>
  );
};
